import React from 'react';
import { getImageUrl } from '@/utils/imageUrl';
import { cn } from '@/lib/utils';
import { getSegmentIcon } from './constants';

type ContainerTypeThumbnailProps = {
  imageUrl?: string | null;
  hexCode?: string | null;
  /** Container segment, used to pick the fallback category icon. */
  segment?: string | null;
  /** Rendered box size in px. */
  size?: number;
  /** Lucide icon size in px (defaults to ~half the box). */
  iconSize?: number;
  onClick?: () => void;
  className?: string;
  title?: string;
};

/**
 * Square container thumbnail. Shows the container photo (`imageUrl`) when
 * present, otherwise the segment's category icon as a fallback. A faint overlay
 * of the box colour (`hexCode` at ~30% opacity) sits on top in both cases —
 * mirroring the existing `ContainerTypeDisplay` behaviour.
 */
export default function ContainerTypeThumbnail({
  imageUrl,
  hexCode,
  segment,
  size = 40,
  iconSize,
  onClick,
  className,
  title,
}: ContainerTypeThumbnailProps) {
  const resolvedIconSize = iconSize ?? Math.round(size / 2);

  return (
    <div
      className={cn(
        'tw:relative tw:flex tw:flex-none tw:items-center tw:justify-center tw:overflow-hidden tw:rounded-md tw:border tw:border-border tw:bg-muted tw:text-muted-foreground',
        onClick && 'tw:cursor-pointer',
        className,
      )}
      style={{ width: size, height: size }}
      onClick={onClick}
      title={title}
    >
      {imageUrl ? (
        <img
          src={getImageUrl(size, imageUrl)}
          alt={title ?? ''}
          className="tw:h-full tw:w-full tw:object-cover"
        />
      ) : (
        React.createElement(getSegmentIcon(segment), {
          size: resolvedIconSize,
          strokeWidth: 1.75,
        })
      )}
      {hexCode && (
        <div
          className="tw:pointer-events-none tw:absolute tw:inset-0 tw:opacity-30"
          style={{ backgroundColor: `#${hexCode}` }}
        />
      )}
    </div>
  );
}
